import { Injectable } from "@angular/core";
import { Router } from "@angular/router";
import { LaunchDarklyService, LD_FLAG } from "@core-services/launch-darkly/launch-darkly.service";
import { CustodyAccountStateService } from "@modules/+new/custody_account/domain/+state/custody_account.state.service";
import { CUSTODY_ACCOUNT_OPENING_MODAL } from "@modules/+new/custody_account/domain/entities/custody_account.model";
import { CustodyAccountVerifiedEligibility } from "@modules/+new/custody_account/domain/entities/custody_account_eligibility.model";
import { CustodyAccountVerificationStatus } from "@modules/+new/custody_account/domain/entities/custody_account_verification.model";
import { CustodyAccountOpeningStep } from "@modules/+new/custody_account/features/cam-modal/cam-modal.model";
import { CustodyAccountRemote } from "@modules/+new/custody_account/infrastructure/custody_account.remote";
import { combineLatest, Observable, of } from "rxjs";
import { map, switchMap, take, tap } from "rxjs/operators";

@Injectable({
	providedIn: "root"
})
export class CustodyAccountFacade {
	constructor(
		private readonly custodyAccountStateService: CustodyAccountStateService,
		private readonly custodyAccountRemote: CustodyAccountRemote,
		private readonly launchDarklyService: LaunchDarklyService,
		private readonly router: Router
	) {}

	private steps: Partial<Record<CustodyAccountVerifiedEligibility, CustodyAccountOpeningStep>> = {
		[CustodyAccountVerifiedEligibility.ELIGIBLE]: CustodyAccountOpeningStep.ELIGIBLE,
		[CustodyAccountVerifiedEligibility.MISSING_REQUIRED_DATA]: CustodyAccountOpeningStep.NOT_ELIGIBLE,
		[CustodyAccountVerifiedEligibility.NOT_ELIGIBLE]: CustodyAccountOpeningStep.NOT_ELIGIBLE
	};

	public redirectToNextStep(step: CustodyAccountOpeningStep): void {
		const modal = `${CUSTODY_ACCOUNT_OPENING_MODAL}/${step}`;
		this.router.navigate(
			[
				"/home",
				{
					outlets: {
						[CUSTODY_ACCOUNT_OPENING_MODAL]: modal
					}
				}
			],
			{ skipLocationChange: true }
		);
	}

	private getEligibilityStatus$(): Observable<boolean> {
		return combineLatest([
			this.launchDarklyService.isFeatureActive$(LD_FLAG.STOCKS_20_CUSTODY_ACCOUNT_OPENING),
			this.custodyAccountStateService.getUserEligibility$()
		]).pipe(map(([ldActive, isEligible]) => ldActive && isEligible));
	}

	public checkEligibility$(): Observable<boolean> {
		let ldActive = false;
		this.launchDarklyService
			.isFeatureActive$(LD_FLAG.STOCKS_20_CUSTODY_ACCOUNT_OPENING)
			.subscribe((flag: boolean) => (ldActive = flag));
		if (!ldActive) {
			return of(false);
		}
		if (this.custodyAccountStateService.isEligibilityLoaded()) {
			return this.getEligibilityStatus$();
		}
		return this.custodyAccountRemote.checkPreEligibility$().pipe(
			tap((isEligible: boolean) => {
				this.custodyAccountStateService.setIsEligibilityLoaded(true);
				this.custodyAccountStateService.setUserEligibility(isEligible);
			}),
			switchMap(() => this.getEligibilityStatus$())
		);
	}

	private recheckEligibility(): void {
		this.custodyAccountRemote
			.checkVerifiedEligibility$()
			.pipe(take(1))
			.subscribe((eligibility: CustodyAccountVerifiedEligibility) => {
				this.custodyAccountStateService.setCAVerificationLoading(false);
				if (eligibility !== CustodyAccountVerifiedEligibility.UNKNOWN) {
					this.redirectToNextStep(this.steps[eligibility]);
					this.custodyAccountStateService.setUserVerifiedEligibility(eligibility);
				}
			});
	}

	public checkVerificationStatus(): void {
		this.custodyAccountStateService.setCAVerificationLoading(true);
		this.custodyAccountRemote
			.checkCustodyAccountVerificationStatus$()
			.pipe(take(1))
			.subscribe((verificationStatus: CustodyAccountVerificationStatus) => {
				if (verificationStatus) {
					if (verificationStatus === CustodyAccountVerificationStatus.VERIFIED) {
						this.recheckEligibility();
					}
					if (verificationStatus === CustodyAccountVerificationStatus.NOT_VERIFIED) {
						this.redirectToNextStep(CustodyAccountOpeningStep.NOT_VERIFIED);
						this.custodyAccountStateService.setCAVerificationLoading(false);
					}
				} else {
					this.custodyAccountStateService.setCAVerificationLoading(false);
				}
			});
	}

	public isCAVerificationLoading$(): Observable<boolean> {
		return this.custodyAccountStateService.isUserCAVerificationStatusLoading$();
	}

	public getVerifiedEligibility$(): Observable<CustodyAccountVerifiedEligibility> {
		return this.custodyAccountStateService.getUserVerifiedEligibility$();
	}
}
